import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  FileText,
  Save,
  AlertCircle,
  Plus,
  Trash2,
  Pill,
  Activity,
  Stethoscope,
  Clock,
  CreditCard,
  Receipt
} from 'lucide-react'
import { getPatient, updatePatient } from '../../api/insuranceAPI'
import GenerateReceipt from '../../components/patients/GenerateReceipt'
import IPFinalBill from '../../components/patients/IPFinalBill'

const PatientEdit = () => {
  const { id: patientId } = useParams()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [activeSection, setActiveSection] = useState('personal')
  const [deletedAdmissions, setDeletedAdmissions] = useState([])

  // Form data state
  const [formData, setFormData] = useState({
    // Personal Information
    full_name: '',
    uhid_no: '',
    ip_no: '',
    dob: '',
    gender: '',
    age: '',
    aadhar_no: '',
    mobile: '',
    email: '',
    address: '',
    center_code: '',

    // OPD Records
    opd_consultation_records: [],

    // Admissions
    admissions: [],

    // Prescriptions
    prescriptions: []
  })

  useEffect(() => {
    fetchPatient()
  }, [patientId])

  const fetchPatient = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getPatient(patientId)
      const data = response.data.data
      setPatient(data)

      // Initialize form data with patient data
      setFormData({
        full_name: data.full_name || '',
        uhid_no: data.uhid_no || '',
        ip_no: data.ip_no || '',
        dob: data.dob ? data.dob.split('T')[0] : '',
        gender: data.gender || '',
        age: data.age || '',
        aadhar_no: data.aadhar_no || '',
        mobile: data.mobile || '',
        // email: data.email || '',
        email: 'srinivas.s@epionepaincare.com',
        address: data.address || '',
        center_code: data.center_code || '',
        opd_consultation_records: (data.opd_consultation_records || []).filter(record => {
          return record.chief_complaints && record.chief_complaints.trim() !== '';
        }),
        admissions: (data.admissions || []).filter(admission => {
          // Filter out incomplete records where all dates are null AND body_parts is null
          const hasAnyDate = admission.date_of_admission || admission.date_of_procedure || admission.date_of_discharge;
          const hasTreatment = admission.body_parts && String(admission.body_parts).trim() !== '' && String(admission.body_parts).toLowerCase() !== 'null';
          return hasAnyDate || hasTreatment;
        }),
        prescriptions: data.prescriptions || [] // Will be empty initially
      })
    } catch (err) {
      console.error('Failed to fetch patient:', err)
      setError(err.response?.data?.error || 'Failed to load patient details')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleAdmissionChange = (index, field, value) => {
    const updatedAdmissions = [...formData.admissions]
    updatedAdmissions[index][field] = value
    setFormData(prev => ({ ...prev, admissions: updatedAdmissions }))
  }

  const handleOPDRecordChange = (index, field, value) => {
    const updatedRecords = [...formData.opd_consultation_records]
    updatedRecords[index][field] = value
    setFormData(prev => ({ ...prev, opd_consultation_records: updatedRecords }))
  }

  const addOPDRecord = () => {
    const newRecord = {
      record_id: `temp_${Date.now()}`,
      chief_complaints: '',
      history_of_illness: '',
      examination: '',
      provisional_diagnosis: '',
      pain_scale: '',
      investigation: '',
      advice: '',
      created_at: new Date().toISOString()
    }
    setFormData(prev => ({
      ...prev,
      opd_consultation_records: [...prev.opd_consultation_records, newRecord]
    }))
  }

  const removeOPDRecord = (index) => {
    const record = formData.opd_consultation_records[index]
    const recordDescription = record.chief_complaints || `Record #${index + 1}`
    
    if (!window.confirm(`Are you sure you want to delete "${recordDescription}"?\n\nThis action cannot be undone.`)) {
      return
    }
    
    const updatedRecords = formData.opd_consultation_records.filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, opd_consultation_records: updatedRecords }))
  }

  const addAdmission = () => {
    const newAdmission = {
      admission_id: `temp_${Date.now()}`, // Temporary ID for new admissions (non-numeric to avoid backend confusion)
      ip_no: '',
      date_of_admission: '',
      time_of_admission: '',
      date_of_procedure: '',
      time_of_procedure: '',
      date_of_discharge: '',
      time_of_discharge: '',
      pre_procedure_diagnosis: '',
      detail_of_procedure: '',
      treatment_planned: '',
      surgeon: '',
      course_of_hospital_stay: '',
      discharge_instruction: '',
      discharge_status: 'active',
      review_date: '',
      reason_for_next_visit: '',
      discharge_medicines: []
    }
    setFormData(prev => ({
      ...prev,
      admissions: [...prev.admissions, newAdmission]
    }))
  }

  const removeAdmission = (index) => {
    const admission = formData.admissions[index]
    const admissionDescription = admission.ip_no || admission.body_parts || `Admission #${index + 1}`
    
    if (!window.confirm(`Are you sure you want to delete "${admissionDescription}"?\n\nThis action cannot be undone.`)) {
      return
    }
    
    // Track deleted admission ID if it's an existing admission (not temporary)
    if (admission.admission_id && !String(admission.admission_id).startsWith('temp_')) {
      setDeletedAdmissions(prev => [...prev, admission.admission_id])
    }
    
    const updatedAdmissions = formData.admissions.filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, admissions: updatedAdmissions }))
  }

  const addDischargeMedicine = (admissionIndex) => {
    const updatedAdmissions = [...formData.admissions]
    if (!updatedAdmissions[admissionIndex].discharge_medicines) {
      updatedAdmissions[admissionIndex].discharge_medicines = []
    }
    updatedAdmissions[admissionIndex].discharge_medicines.push({
      medicine_name: '',
      dose: '',
      frequency: '',
      duration_days: '',
      morning: false,
      afternoon: false,
      night: false
    })
    setFormData(prev => ({ ...prev, admissions: updatedAdmissions }))
  }

  const updateDischargeMedicine = (admissionIndex, medicineIndex, field, value) => {
    const updatedAdmissions = [...formData.admissions]
    updatedAdmissions[admissionIndex].discharge_medicines[medicineIndex][field] = value
    setFormData(prev => ({ ...prev, admissions: updatedAdmissions }))
  }

  const removeDischargeMedicine = (admissionIndex, medicineIndex) => {
    const updatedAdmissions = [...formData.admissions]
    updatedAdmissions[admissionIndex].discharge_medicines.splice(medicineIndex, 1)
    setFormData(prev => ({ ...prev, admissions: updatedAdmissions }))
  }

  const handlePrescriptionChange = (groupIndex, prescriptionIndex, field, value) => {
    const updatedPrescriptions = [...formData.prescriptions]
    updatedPrescriptions[groupIndex][prescriptionIndex][field] = value
    setFormData(prev => ({ ...prev, prescriptions: updatedPrescriptions }))
  }

  const addPrescriptionGroup = () => {
    const newGroup = [{
      medicine_name: '',
      dose: '',
      frequency: '',
      duration_days: '',
      morning: false,
      afternoon: false,
      night: false,
      additional_notes: '',
      prescription_text: 'OPD Prescription',
      created_at: new Date().toISOString()
    }]
    setFormData(prev => ({
      ...prev,
      prescriptions: [...prev.prescriptions, newGroup]
    }))
  }

  const addMedicineToGroup = (groupIndex) => {
    const updatedPrescriptions = [...formData.prescriptions]
    updatedPrescriptions[groupIndex].push({
      medicine_name: '',
      dose: '',
      frequency: '',
      duration_days: '',
      morning: false,
      afternoon: false,
      night: false,
      additional_notes: '',
      prescription_text: 'OPD Prescription',
      created_at: new Date().toISOString()
    })
    setFormData(prev => ({ ...prev, prescriptions: updatedPrescriptions }))
  }

  const removePrescriptionGroup = (groupIndex) => {
    const updatedPrescriptions = formData.prescriptions.filter((_, i) => i !== groupIndex)
    setFormData(prev => ({ ...prev, prescriptions: updatedPrescriptions }))
  }

  const removeMedicineFromGroup = (groupIndex, medicineIndex) => {
    const updatedPrescriptions = [...formData.prescriptions]
    updatedPrescriptions[groupIndex].splice(medicineIndex, 1)
    if (updatedPrescriptions[groupIndex].length === 0) {
      updatedPrescriptions[groupIndex].splice(groupIndex, 1)
    }
    setFormData(prev => ({ ...prev, prescriptions: updatedPrescriptions }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      // Prepare data for submission
      const updateData = {
        ...formData,
        // Include deleted admission IDs
        deleted_admissions: deletedAdmissions,
        // Convert empty strings to null for optional fields
        ip_no: formData.ip_no || null,
        aadhar_no: formData.aadhar_no || null,
        dob: formData.dob || null,
        age: formData.age ? parseInt(formData.age) : null,
        // Include OPD consultation records
        opd_consultation_records: formData.opd_consultation_records
      }

      await updatePatient(patientId, updateData)
      navigate(`/patients/${patientId}/view`, {
        state: { message: 'Patient updated successfully' }
      })
    } catch (err) {
      console.error('Failed to update patient:', err)
      const errorObj = err.response?.data?.error
      const errorMsg = errorObj?.message || errorObj || 'Failed to update patient'
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const SectionButton = ({ id, icon: Icon, title, isActive }) => (
    <button
      type="button"
      onClick={() => setActiveSection(id)}
      className={`flex items-center px-4 py-3 rounded-lg transition-all ${
        isActive
          ? 'bg-blue-600 text-white shadow-md'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
      }`}
    >
      <Icon className="mr-2" size={18} />
      <span className="font-medium">{title}</span>
    </button>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-600 mr-3" size={32} />
        <span className="text-gray-600">Loading patient details...</span>
      </div>
    )
  }

  if (error && !patient) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center">
        <AlertCircle className="text-red-600 mr-3" size={24} />
        <div>
          <h3 className="font-semibold text-red-800">Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    )
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <User className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-lg font-medium text-gray-800">Patient Not Found</h3>
      </div>
    )
  }

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/patients/${patientId}/view`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="text-gray-600" size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Edit Patient: {patient.full_name}</h1>
            <p className="text-gray-600">UHID: {patient.uhid_no} | Patient ID: {patient.patient_id}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/patients/${patientId}/view`)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save className="mr-2" size={18} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <SectionButton
          id="personal"
          icon={User}
          title="Personal Info"
          isActive={activeSection === 'personal'}
        />
        <SectionButton
          id="medical"
          icon={Stethoscope}
          title="OPD Records"
          isActive={activeSection === 'medical'}
        />
        <SectionButton
          id="admissions"
          icon={Activity}
          title="Admissions"
          isActive={activeSection === 'admissions'}
        />
        <SectionButton
          id="prescriptions"
          icon={Pill}
          title="Prescriptions"
          isActive={activeSection === 'prescriptions'}
        />
        <SectionButton
          id="billing"
          icon={CreditCard}
          title="Billing"
          isActive={activeSection === 'billing'}
        />
        <SectionButton
          id="ip-final-bill"
          icon={CreditCard}
          title="IP FINAL BILL"
          isActive={activeSection === 'ip-final-bill'}
        />
        <SectionButton
          id="documents"
          icon={FileText}
          title="Documents"
          isActive={activeSection === 'documents'}
        />
        <SectionButton
          id="timeline"
          icon={Clock}
          title="Timeline"
          isActive={activeSection === 'timeline'}
        />
      </div>

      {/* Form Sections */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {activeSection === 'personal' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <User className="mr-2 text-blue-600" size={24} />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UHID *</label>
                  <input
                    type="text"
                    name="uhid_no"
                    value={formData.uhid_no}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IP Number</label>
                  <input
                    type="text"
                    name="ip_no"
                    value={formData.ip_no}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="150"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input
                    type="text"
                    name="aadhar_no"
                    value={formData.aadhar_no}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    pattern="[0-9]{12}"
                    title="Aadhar number should be 12 digits"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    pattern="[0-9]{10}"
                    title="Mobile number should be 10 digits"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Center Code *</label>
                  <input
                    type="text"
                    name="center_code"
                    value={formData.center_code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {activeSection === 'medical' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Stethoscope className="mr-2 text-blue-600" size={24} />
                OPD Records
              </h2>
              <button
                type="button"
                onClick={addOPDRecord}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus size={18} className="mr-2" />
                Add OPD Record
              </button>
            </div>

            <div className="space-y-6">
              {formData.opd_consultation_records.length > 0 ? (
                formData.opd_consultation_records.map((record, index) => (
                  <div key={record.record_id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-gray-800">OPD Record #{index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeOPDRecord(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Chief Complaints</label>
                        <textarea
                          value={record.chief_complaints || ''}
                          onChange={(e) => handleOPDRecordChange(index, 'chief_complaints', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Enter chief complaints..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">History of Illness</label>
                        <textarea
                          value={record.history_of_illness || ''}
                          onChange={(e) => handleOPDRecordChange(index, 'history_of_illness', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Enter history of illness..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Examination Findings</label>
                        <textarea
                          value={record.examination || ''}
                          onChange={(e) => handleOPDRecordChange(index, 'examination', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Enter examination findings..."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Provisional Diagnosis</label>
                          <textarea
                            value={record.provisional_diagnosis || ''}
                            onChange={(e) => handleOPDRecordChange(index, 'provisional_diagnosis', e.target.value)}
                            rows={2}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Enter provisional diagnosis..."
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Pain Scale</label>
                          <input
                            type="text"
                            value={record.pain_scale || ''}
                            onChange={(e) => handleOPDRecordChange(index, 'pain_scale', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="e.g., 8/10"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Investigations</label>
                        <textarea
                          value={record.investigation || ''}
                          onChange={(e) => handleOPDRecordChange(index, 'investigation', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Enter investigation results..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Medical Advice</label>
                        <textarea
                          value={record.advice || ''}
                          onChange={(e) => handleOPDRecordChange(index, 'advice', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Enter medical advice..."
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No OPD records. Click "Add OPD Record" to create one.
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'admissions' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Activity className="mr-2 text-blue-600" size={24} />
                Admission History
              </h2>
              <button
                type="button"
                onClick={addAdmission}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus size={18} className="mr-2" />
                Add Admission
              </button>
            </div>

            <div className="space-y-6">
              {formData.admissions.map((admission, index) => (
                <div key={admission.admission_id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-gray-800">Admission #{index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeAdmission(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">IP Number</label>
                      <input
                        type="text"
                        value={admission.ip_no || ''}
                        onChange={(e) => handleAdmissionChange(index, 'ip_no', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Admission Date</label>
                      <input
                        type="date"
                        value={admission.date_of_admission || ''}
                        onChange={(e) => handleAdmissionChange(index, 'date_of_admission', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Admission Time</label>
                      <input
                        type="time"
                        value={admission.time_of_admission || ''}
                        onChange={(e) => handleAdmissionChange(index, 'time_of_admission', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Procedure Date</label>
                      <input
                        type="date"
                        value={admission.date_of_procedure || ''}
                        onChange={(e) => handleAdmissionChange(index, 'date_of_procedure', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Pre-procedure Diagnosis</label>
                      <textarea
                        value={admission.pre_procedure_diagnosis || ''}
                        onChange={(e) => handleAdmissionChange(index, 'pre_procedure_diagnosis', e.target.value)}
                        rows={2}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Procedure Details</label>
                      <textarea
                        value={admission.detail_of_procedure || ''}
                        onChange={(e) => handleAdmissionChange(index, 'detail_of_procedure', e.target.value)}
                        rows={2}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Treatment Planned</label>
                      <textarea
                        value={admission.treatment_planned || ''}
                        onChange={(e) => handleAdmissionChange(index, 'treatment_planned', e.target.value)}
                        rows={2}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Surgeon</label>
                      <input
                        type="text"
                        value={admission.surgeon || ''}
                        onChange={(e) => handleAdmissionChange(index, 'surgeon', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Discharge Date</label>
                      <input
                        type="date"
                        value={admission.date_of_discharge || ''}
                        onChange={(e) => handleAdmissionChange(index, 'date_of_discharge', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Discharge Time</label>
                      <input
                        type="time"
                        value={admission.time_of_discharge || ''}
                        onChange={(e) => handleAdmissionChange(index, 'time_of_discharge', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select
                        value={admission.discharge_status || 'active'}
                        onChange={(e) => handleAdmissionChange(index, 'discharge_status', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="discharged">Discharged</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Course of Hospital Stay</label>
                    <input
                      type="text"
                      value={admission.course_of_hospital_stay || ''}
                      onChange={(e) => handleAdmissionChange(index, 'course_of_hospital_stay', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="e.g., 5 days"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Discharge Instructions</label>
                    <textarea
                      value={admission.discharge_instruction || ''}
                      onChange={(e) => handleAdmissionChange(index, 'discharge_instruction', e.target.value)}
                      rows={3}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Review Date</label>
                      <input
                        type="date"
                        value={admission.review_date || ''}
                        onChange={(e) => handleAdmissionChange(index, 'review_date', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reason for Next Visit</label>
                      <input
                        type="text"
                        value={admission.reason_for_next_visit || ''}
                        onChange={(e) => handleAdmissionChange(index, 'reason_for_next_visit', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Reason for review visit"
                      />
                    </div>
                  </div>

                  {/* Discharge Medicines */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-xs font-medium text-gray-600">Discharge Medicines</label>
                      <button
                        type="button"
                        onClick={() => addDischargeMedicine(index)}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Medicine
                      </button>
                    </div>

                    {admission.discharge_medicines && admission.discharge_medicines.map((medicine, medIndex) => (
                      <div key={medIndex} className="bg-gray-50 rounded p-2 mb-2">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
                          <div>
                            <input
                              type="text"
                              placeholder="Medicine Name"
                              value={medicine.medicine_name || ''}
                              onChange={(e) => updateDischargeMedicine(index, medIndex, 'medicine_name', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="Dose"
                              value={medicine.dose || ''}
                              onChange={(e) => updateDischargeMedicine(index, medIndex, 'dose', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="Frequency"
                              value={medicine.frequency || ''}
                              onChange={(e) => updateDischargeMedicine(index, medIndex, 'frequency', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="Duration"
                              value={medicine.duration_days || ''}
                              onChange={(e) => updateDischargeMedicine(index, medIndex, 'duration_days', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <div className="flex items-center space-x-1">
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={medicine.morning || false}
                                onChange={(e) => updateDischargeMedicine(index, medIndex, 'morning', e.target.checked)}
                                className="mr-1"
                              />
                              M
                            </label>
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={medicine.afternoon || false}
                                onChange={(e) => updateDischargeMedicine(index, medIndex, 'afternoon', e.target.checked)}
                                className="mr-1"
                              />
                              A
                            </label>
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={medicine.night || false}
                                onChange={(e) => updateDischargeMedicine(index, medIndex, 'night', e.target.checked)}
                                className="mr-1"
                              />
                              N
                            </label>
                            <button
                              type="button"
                              onClick={() => removeDischargeMedicine(index, medIndex)}
                              className="text-red-600 hover:text-red-800 ml-2"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {formData.admissions.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Admission Records</h3>
                  <p className="text-gray-600">Click "Add Admission" to add admission history for this patient.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'prescriptions' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Pill className="mr-2 text-blue-600" size={24} />
                Prescription History
              </h2>
              <button
                type="button"
                onClick={addPrescriptionGroup}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus size={18} className="mr-2" />
                Add Prescription
              </button>
            </div>

            <div className="space-y-6">
              {formData.prescriptions.map((prescriptionGroup, groupIndex) => (
                <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-gray-800">
                      Prescription Group #{groupIndex + 1}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => addMedicineToGroup(groupIndex)}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Medicine
                      </button>
                      <button
                        type="button"
                        onClick={() => removePrescriptionGroup(groupIndex)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {prescriptionGroup.map((prescription, prescriptionIndex) => (
                    <div key={prescriptionIndex} className="bg-gray-50 rounded p-3 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Medicine Name</label>
                          <input
                            type="text"
                            value={prescription.medicine_name || ''}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'medicine_name', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Dose</label>
                          <input
                            type="text"
                            value={prescription.dose || ''}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'dose', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                          <input
                            type="text"
                            value={prescription.frequency || ''}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'frequency', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                          <input
                            type="text"
                            value={prescription.duration_days || ''}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'duration_days', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 mb-3">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={prescription.morning || false}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'morning', e.target.checked)}
                            className="mr-2"
                          />
                          Morning
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={prescription.afternoon || false}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'afternoon', e.target.checked)}
                            className="mr-2"
                          />
                          Afternoon
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={prescription.night || false}
                            onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'night', e.target.checked)}
                            className="mr-2"
                          />
                          Night
                        </label>
                        <button
                          type="button"
                          onClick={() => removeMedicineFromGroup(groupIndex, prescriptionIndex)}
                          className="text-red-600 hover:text-red-800 ml-auto"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Additional Notes</label>
                        <textarea
                          value={prescription.additional_notes || ''}
                          onChange={(e) => handlePrescriptionChange(groupIndex, prescriptionIndex, 'additional_notes', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {formData.prescriptions.length === 0 && (
                <div className="text-center py-8">
                  <Pill className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Prescriptions</h3>
                  <p className="text-gray-600">Click "Add Prescription" to add prescription history for this patient.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'documents' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileText className="mr-2 text-blue-600" size={24} />
              Medical Documents
            </h2>

            <div className="text-center py-8">
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Document Management</h3>
              <p className="text-gray-600 mb-4">Document upload and management features will be implemented in the next phase.</p>
              <p className="text-sm text-gray-500">This will include discharge summaries, consent forms, and other medical documents.</p>
            </div>
          </div>
        )}

        {activeSection === 'timeline' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <Clock className="mr-2 text-blue-600" size={24} />
              Medical Timeline
            </h2>

            <div className="text-center py-8">
              <Clock className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Timeline View</h3>
              <p className="text-gray-600 mb-4">Interactive timeline visualization will be implemented in the next phase.</p>
              <p className="text-sm text-gray-500">This will show chronological medical events, treatments, and follow-ups.</p>
            </div>
          </div>
        )}
      </div>
    </form>

    {/* Billing Section - Outside main form to avoid nested forms */}
    {activeSection === 'billing' && (
      <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            {/* <Receipt className="mr-2 text-blue-600" size={24} /> */}
            Billing & Receipts
          </h2>
          
          <GenerateReceipt patient={patient} />
        </div>
      </div>
    )}

    {activeSection === 'ip-final-bill' && (
      <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            {/* <Receipt className="mr-2 text-blue-600" size={24} /> */}
            IP FINAL BILL
          </h2>
          
          <IPFinalBill patient={patient} />
        </div>
      </div>
    )}
  </>
  )
}

export default PatientEdit
